import moment from 'moment';
import { useAppDispatch } from 'src/redux/hooks';

import { QUOTE_KEYS } from '../createQuote.const';
import { setCreateQuoteStep } from '../CreateQuote.slice';

export const useSaveAndNextStep = () => {
    const dispatch = useAppDispatch();

    const onSaveNext = (data: any, currentStep: string) => {
        switch (currentStep) {
            case QUOTE_KEYS.QUOTE_DETAILS:
                dispatch(setCreateQuoteStep(QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS));
                break;
        }
    };

    return onSaveNext;
};

export function createAddressPayload<T extends Record<string, any>>(source: T, keyMap: Record<string, keyof T | string>): Record<string, any> {
    return Object.entries(keyMap).reduce((acc, [targetKey, sourceKey]) => {
        acc[targetKey] = source?.[sourceKey as keyof T] ?? null;

        return acc;
    }, {} as Record<string, any>);
}

/**
 * Get date category (today, current week, next week, later)
 * @param date - The date to categorize
 * @param timezone - The timezone to use for calculations
 * @returns 'today' | 'current_week' | 'next_week' | 'later' | null
 */
export const getDateCategory = (date: Date | null | string, timezone: string): 'today' | 'current_week' | 'next_week' | 'later' | null => {
    if (!date) return null;

    const dateObj = date instanceof Date ? date : new Date(date);
    const pickupMoment = moment.utc([dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()]);
    const todayMoment = moment.tz(timezone).startOf('day');
    const currentWeekStart = moment.tz(timezone).startOf('week');
    const currentWeekEnd = moment.tz(timezone).endOf('week');
    const nextWeekStart = moment.tz(timezone).add(1, 'week').startOf('week');
    const nextWeekEnd = moment.tz(timezone).add(1, 'week').endOf('week');

    const pickupDateString = pickupMoment.format('YYYY-MM-DD');
    const todayString = todayMoment.format('YYYY-MM-DD');

    if (pickupDateString === todayString) {
        return 'today';
    }

    if (pickupMoment.isSameOrAfter(currentWeekStart, 'day') && pickupMoment.isSameOrBefore(currentWeekEnd, 'day')) {
        return 'current_week';
    }

    if (pickupMoment.isSameOrAfter(nextWeekStart, 'day') && pickupMoment.isSameOrBefore(nextWeekEnd, 'day')) {
        return 'next_week';
    }

    return 'later';
};

/**
 * Generate dynamic day pair label (e.g., "Monday 2nd or Tuesday 3rd")
 * @param date - The date to generate label for
 * @returns Formatted day pair string
 */
export const getDayPairLabel = (date: Date | null | string): string => {
    if (!date) return '';

    const dateObj = date instanceof Date ? date : new Date(date);
    const pickupMoment = moment([dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()]);
    const nextDayMoment = pickupMoment.clone().add(1, 'day');

    const currentDayName = pickupMoment.format('ddd');
    const currentDayNumber = pickupMoment.format('Do');
    const nextDayName = nextDayMoment.format('ddd');
    const nextDayNumber = nextDayMoment.format('Do');

    return `${currentDayName} ${currentDayNumber} or ${nextDayName} ${nextDayNumber}`;
};

/**
 * Generate week range label (e.g., "Anytime Feb 3rd to Feb 9th")
 * @param date - The date to generate week range for
 * @returns Object with formatted week range string and startDate in YYYY-MM-DD format
 */
export const getWeekRangeLabel = (date: Date | null | string): { label: string; startDate: string } => {
    if (!date) return { label: '', startDate: '' };

    const dateObj = date instanceof Date ? date : new Date(date);
    const pickupMoment = moment([dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()]);
    const weekStart = pickupMoment.clone().startOf('week');
    const weekEnd = pickupMoment.clone().endOf('week');

    const startDate = weekStart.format('MMM Do');
    const endDate = weekEnd.format('MMM Do');
    const startDateFormatted = weekStart.format('YYYY-MM-DD');

    return {
        label: `Between ${startDate} - ${endDate}`,
        startDate: startDateFormatted,
    };
};

/**
 * Checks if a given date is today in the specified timezone
 * @param date - The date to check
 * @param timezone - The timezone to use for comparison
 * @returns boolean
 */
export const isPickupDateToday = (date: Date | null | string, timezone: string): boolean => {
    if (!date) return false;
    const dateObj = date instanceof Date ? date : new Date(date);

    const pickupMoment = moment.utc([dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()]);

    const todayMoment = moment.tz(timezone).startOf('day');
    const todayString = todayMoment.format('YYYY-MM-DD');
    const pickupDateString = pickupMoment.format('YYYY-MM-DD');

    return pickupDateString === todayString;
};
